import { useEffect } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import useAPIRequest from 'src/hooks/useAPIRequest'
import { routes } from 'src/routes/routes'
import { getFromStorage, saveToStorage } from 'src/shared/helpers/sessionStorageHelpers'
import { UserActions, useUser, useUserDispatch } from 'src/state/userState'

import apiClient from './apiClient'
import { isMFAResponse, isMultiTenantResponse } from './typeguards'
import { LoginResponse } from './types'

import { AuthenticationResult } from '@azure/msal-browser'

const useLogin = () => {
    const userDispatch = useUserDispatch()
    const { authenticated, info } = useUser()
    const { id: userId } = info

    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const { partialAuthToken } = getFromStorage('user')

    const { callAPI: getUserInfo, result: userInfo } = useAPIRequest(apiClient.getUserInfo)
    const {
        callAPI: loginSSOUser,
        result: SSOLogin,
        loading: loggingInSSOUser,
        error: loginSSOUserError,
        reset: resetSSOLogin,
    } = useAPIRequest(apiClient.loginSSOUser)
    const {
        callAPI: loginCredentialUser,
        result: credentialLogin,
        loading: loggingInCredentialUser,
        error: loginCredentialUserError,
        reset: resetCredentialLogin,
    } = useAPIRequest(apiClient.loginCredentialUser)
    const {
        callAPI: loginMFAUser,
        result: MFAlogin,
        loading: loggingInMFAUser,
        error: loginMFAUserError,
        reset: resetMFAlogin,
    } = useAPIRequest(apiClient.loginMFAUser)
    const {
        callAPI: loginMultiTenantUser,
        result: multiTenantLogin,
        loading: loggingInMultiTenantUser,
        error: loginMultiTenantUserError,
        reset: resetMultiTenantLogin,
    } = useAPIRequest(apiClient.loginMultiTenantUser)

    const {
        callAPI: validateRedirect,
        loading: validateRedirectLoading,
        error: validateRedirectError,
    } = useAPIRequest(apiClient.validateRedirect)

    const resetAllAPIRequests = () => {
        resetSSOLogin()
        resetCredentialLogin()
        resetMFAlogin()
        resetMultiTenantLogin()
    }

    const loading =
        loggingInSSOUser ||
        loggingInCredentialUser ||
        loggingInMFAUser ||
        loggingInMultiTenantUser ||
        validateRedirectLoading
    const errors = {
        loginSSOUserError,
        loginCredentialUserError,
        loginMFAUserError,
        loginMultiTenantUserError,
        validateRedirectError,
    }

    const handleLogin = async (loginData: LoginResponse) => {
        const currentSessionState = getFromStorage('user') ?? {}
        saveToStorage('user', { ...currentSessionState, ...loginData })

        if (isMFAResponse(loginData) || isMultiTenantResponse(loginData)) {
            return
        }

        let isValidRedirect = false
        const redirectUrl = searchParams.get('redirectUrl')
        if (redirectUrl) {
            const result = await validateRedirect({ redirectUrl })
            isValidRedirect = result.valid
        }

        if (!redirectUrl || (redirectUrl && isValidRedirect)) {
            userDispatch({ type: UserActions.LOG_IN })
        }
    }

    useEffect(() => {
        if (credentialLogin) {
            handleLogin(credentialLogin)
        }
    }, [credentialLogin])

    useEffect(() => {
        if (SSOLogin) {
            handleLogin(SSOLogin)
        }
    }, [SSOLogin])

    useEffect(() => {
        if (MFAlogin) {
            handleLogin(MFAlogin)
        }
    }, [MFAlogin])

    useEffect(() => {
        if (multiTenantLogin) {
            handleLogin(multiTenantLogin)
        }
    }, [multiTenantLogin])

    useEffect(() => {
        const needUserData = authenticated && !userId
        if (needUserData) {
            getUserInfo({})
        }
    }, [authenticated, userId])

    useEffect(() => {
        if (authenticated && userInfo) {
            userDispatch({ type: UserActions.SET_USER_INFO, payload: userInfo })

            const { confirmingDelivery, previousRoute } = location.state || {}
            if (confirmingDelivery) {
                // If the user is confirming a delivery, previousRoute will be /confirm-delivery/;tradeInId
                // In this case, we want to skip the dealer/unit/brand selection
                navigate(previousRoute)
            } else {
                navigate(
                    {
                        pathname: routes.DEALER_SELECTOR,
                        ...(searchParams.toString() ? { search: `?${searchParams.toString()}` } : {}),
                    }, //forwards potential searchparams from the login screen to subsequent screens (e.g. used for SSO login)
                    { ...(location.state ? { state: location.state } : {}) },
                )
            }
        }
    }, [authenticated, userInfo])

    const handleMFALogin = async (code: string) => {
        resetAllAPIRequests()
        await loginMFAUser({ code, token: partialAuthToken })
    }

    const handleSSOLogin = async (responseData: AuthenticationResult, params?: URLSearchParams) => {
        if (params) {
            const combinedParams = new URLSearchParams({
                ...Object.fromEntries(searchParams),
                ...Object.fromEntries(params),
            })
            setSearchParams(combinedParams, { state: location.state })
        }
        resetAllAPIRequests()
        await loginSSOUser({ token: responseData.idToken })
    }

    const handleCredentialLogin = async (username: string, password: string) => {
        resetAllAPIRequests()
        await loginCredentialUser({ username, password })
    }

    const handleMultiTenantLogin = async (id: string) => {
        resetAllAPIRequests()
        await loginMultiTenantUser({ id, token: partialAuthToken })
    }

    return {
        errors,
        loading,
        onLogin: handleCredentialLogin,
        onMultiTenantLogin: handleMultiTenantLogin,
        onSSOLogin: handleSSOLogin,
        onMFALogin: handleMFALogin,
        onValidateRedirect: validateRedirect,
    }
}

export default useLogin
